/**
 * Maidenhead Locator utilities
 */

/**
 * Convert Maidenhead locator to lat/lng
 * @param {string} locator - Maidenhead locator (e.g., "JN36bk")
 * @returns {object} {lat, lng} or null if invalid
 */
export const maidenheadToLatLng = (locator) => {
  if (!locator || locator.length < 4) return null;
  
  const loc = locator.toUpperCase();
  
  // Field (first 2 characters: A-R)
  // Each field is 20° longitude x 10° latitude
  const fieldLng = (loc.charCodeAt(0) - 65) * 20 - 180;
  const fieldLat = (loc.charCodeAt(1) - 65) * 10 - 90;
  
  // Square (next 2 digits: 0-9)
  // Each square is 2° longitude x 1° latitude
  const squareLng = parseInt(loc.charAt(2)) * 2;
  const squareLat = parseInt(loc.charAt(3)) * 1;
  
  let lng = fieldLng + squareLng;
  let lat = fieldLat + squareLat;
  
  // Subsquare (optional, next 2 characters: a-x)
  // Each subsquare is 5' (1/12°) longitude x 2.5' (1/24°) latitude
  if (loc.length >= 6) {
    const subsquareLng = (loc.charCodeAt(4) - 65) * (2 / 24); // 5 minutes = 2°/24
    const subsquareLat = (loc.charCodeAt(5) - 65) * (1 / 24); // 2.5 minutes = 1°/24
    lng += subsquareLng;
    lat += subsquareLat;
    
    // Center of the subsquare
    lng += (2 / 24) / 2; // Half of 5 minutes
    lat += (1 / 24) / 2; // Half of 2.5 minutes
  } else {
    // Center of the 2° x 1° square (4 character locator)
    lng += 1;    // Center of 2° square
    lat += 0.5;  // Center of 1° square
  }
  
  return { lat, lng };
};

/**
 * Convert latitude/longitude to Maidenhead Locator (6 characters)
 */
export const latLngToMaidenhead = (lat, lng) => {
  // Normalize coordinates
  const adjLat = lat + 90;
  const adjLng = lng + 180;

  // Field (A-R)
  const fieldLng = String.fromCharCode(65 + Math.floor(adjLng / 20));
  const fieldLat = String.fromCharCode(65 + Math.floor(adjLat / 10));

  // Square (0-9)
  const squareLng = Math.floor((adjLng % 20) / 2);
  const squareLat = Math.floor((adjLat % 10) / 1);

  // Subsquare (a-x)
  const remainderLng = (adjLng % 2);
  const remainderLat = (adjLat % 1);
  const subsquareLng = String.fromCharCode(97 + Math.floor(remainderLng * 12));
  const subsquareLat = String.fromCharCode(97 + Math.floor(remainderLat * 24));

  return `${fieldLng}${fieldLat}${squareLng}${squareLat}${subsquareLng}${subsquareLat}`;
};

/**
 * Validate if string is a Maidenhead locator (4 or 6 characters)
 */
export const isMaidenheadLocator = (str) => {
  if (!str) return false;
  // Accept 4 characters (JN36) or 6 characters (JN36bk)
  const pattern = /^[A-R]{2}[0-9]{2}([A-X]{2})?$/i;
  return pattern.test(str.trim());
};
